import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UserTypeStep = ({ selectedType, onSelect, onNext, onBack }) => {
  const userTypes = [
    {
      id: 'parent',
      label: 'Parent/Caregiver',
      icon: 'Users',
      description: 'Tracking symptoms for a child or dependent',
      benefits: [
        'Multiple user profiles',
        'Caregiver sharing features',
        'Child-friendly interface options'
      ],
      color: 'teal'
    },
    {
      id: 'elderly',
      label: 'Elderly Patient',
      icon: '/assets/images/MyBodyToday_logo_design2-removebg-preview1-1770673381636.png',
      description: 'Managing personal health conditions',
      benefits: [
        'Large text and accessibility features',
        'Medication tracking',
        'Emergency contact integration'
      ],
      color: 'blue'
    },
    {
      id: 'chronic',
      label: 'Chronic Condition',
      icon: 'Activity',
      description: 'Long-term condition monitoring',
      benefits: [
        'Advanced symptom tracking',
        'Trend analysis and patterns',
        'Doctor report generation'
      ],
      color: 'purple'
    },
    {
      id: 'general',
      label: 'General Adult',
      icon: 'User',
      description: 'General health tracking',
      benefits: [
        'Simple daily check-ins',
        'Health insights',
        'Wellness tracking'
      ],
      color: 'green'
    }
  ];

  const getColorClasses = (color, isSelected) => {
    const colors = {
      teal: isSelected ? 'border-teal-500 bg-teal-50' : 'hover:border-teal-300',
      blue: isSelected ? 'border-blue-500 bg-blue-50' : 'hover:border-blue-300',
      purple: isSelected ? 'border-purple-500 bg-purple-50' : 'hover:border-purple-300',
      green: isSelected ? 'border-green-500 bg-green-50' : 'hover:border-green-300'
    };
    return colors?.[color] || '';
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
          Tell Us About Yourself
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          This helps us personalize your experience and show you the most relevant features
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
        {userTypes?.map((type) => {
          const isSelected = selectedType === type?.id;
          return (
            <button
              key={type?.id}
              onClick={() => onSelect(type?.id)}
              className={`text-left p-6 rounded-xl border-2 transition-all ${
                isSelected
                  ? getColorClasses(type?.color, true) + ' shadow-lg'
                  : 'border-border bg-card ' + getColorClasses(type?.color, false)
              }`}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className={`w-14 h-14 rounded-lg flex items-center justify-center ${
                  isSelected ? `bg-${type?.color}-500 text-white` : 'bg-muted'
                }`}>
                  <Icon name={type?.icon} size={28} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-foreground mb-1">{type?.label}</h3>
                  <p className="text-sm text-muted-foreground">{type?.description}</p>
                </div>
                {isSelected && (
                  <Icon name="CheckCircle2" size={24} className="text-primary flex-shrink-0" />
                )}
              </div>

              <div className="space-y-2">
                <h4 className="text-sm font-medium text-foreground">What you'll get:</h4>
                <ul className="space-y-1">
                  {type?.benefits?.map((benefit, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
        <Button
          variant="outline"
          onClick={onBack}
          iconName="ArrowLeft"
          className="sm:flex-1"
        >
          Back
        </Button>
        <Button
          onClick={onNext}
          disabled={!selectedType}
          iconName="ArrowRight"
          iconPosition="right"
          className="sm:flex-1"
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export default UserTypeStep;